// Response composer — Path A, overhauled.
//
// What changed vs. v1:
//   - Banks of openers / transitions / closers per intent class. Randomised
//     so the same question phrasing twice produces different replies.
//   - A real "stitcher" that uses natural connectives between chunks
//     instead of just joining with periods.
//   - Summary-head pattern when 3+ chunks are cited.
//   - Per-intent follow-up offerings ("Want me to save your contact?")
//     that signal agentic-ness without needing generation.
//   - Yes/no detection + low-confidence hedging preserved from v1.
//
// Still no LLM. The model only embeds. All "writing" you read comes from
// these templates over the retrieved chunks.

import { FULL_CORPUS, type Chunk } from "@/data/corpus";

// Look up canonical chunks directly by id — bypasses retrieval for intents
// where we know exactly which chunks should anchor the response.
function byId(id: string): Chunk | undefined {
  return FULL_CORPUS.find((c) => c.id === id);
}
function byIds(ids: string[]): Chunk[] {
  return ids.map(byId).filter((c): c is Chunk => !!c);
}
// Pull all chunks whose id starts with a prefix — e.g. "plum-*"
function byPrefix(prefix: string): Chunk[] {
  return FULL_CORPUS.filter((c) => c.id.startsWith(prefix));
}

const CONFIDENCE_FLOOR = 0.02;

export type Intent =
  | "greeting"
  | "who-are-you"
  | "model-question"
  | "hire-openai"
  | "hire-general"
  | "contact"
  | "project-specific"
  | "career-experience"
  | "machine-learning"
  | "philosophy"
  | "writing"
  | "education"
  | "skills"
  | "current"
  | "general";

export type ComposeResult = {
  intent: Intent;
  text: string;
  citedChunkIds: string[];
  satireMode: boolean;
  hedged?: boolean;
};

// ──────────────────────── intent classifier ─────────────────────

const INTENT_PATTERNS: Array<{ intent: Intent; re: RegExp }> = [
  { intent: "greeting", re: /^\s*(hi|hello|hey|yo|sup|what's up|gm)\b/i },
  {
    intent: "model-question",
    re: /\b(what (model|llm)|which (model|llm)|are you (an? )?(llm|ai|gpt|claude|opus)|joshlm|powered by|underlying model|are you (real|really)|debug)\b/i,
  },
  {
    intent: "hire-openai",
    re: /\b(openai (job|role|position|hir(e|ing))|(forward.?deployed|ai deployment|solutions engineer)|why (should|would) openai)\b/i,
  },
  {
    intent: "hire-general",
    re: /\b(hir(e|ing)|recruit|interview|offer|salary|notice period|relocat|why should)\b/i,
  },
  {
    intent: "contact",
    re: /\b(contact|email|reach (out|him)|book a (call|meeting)|schedule|how do i (reach|talk|message))\b/i,
  },
  {
    intent: "machine-learning",
    re: /\b(machine learning|\bml\b|deep learning|neural net|classifier|gan|generative adversarial|model training|fine.?tun)\b/i,
  },
  {
    intent: "writing",
    re: /\b(unengineered|substack|blog|essay|writes?|writing|writ(es|ing|e about)|posts?)\b/i,
  },
  {
    intent: "education",
    re: /\b(education|degree|college|school|university|upes|cgpa|silver medal|graduat|gsoc|summer of code)\b/i,
  },
  {
    intent: "skills",
    re: /\b(skills?|stack|tech (stack)?|languages?|frameworks?|tools|what (does|can) he (use|know))\b/i,
  },
  {
    intent: "current",
    re: /\b(current(ly)?|right now|these days|present role|today|at coupang|what is he doing)\b/i,
  },
  {
    intent: "philosophy",
    re: /\b(philosophy|believe|approach|how does he|why does he|his view|on engineering|on ai|think|opinion)\b/i,
  },
  {
    intent: "career-experience",
    re: /\b(experience|career|history|background|worked at|previously|past|plum|visa|coupang|qoala|startup|forward.?deployed)\b/i,
  },
  {
    intent: "project-specific",
    re: /\b(mcp.?jest|aa.?kit|engram|approval protocol|freeport|grain|tongue|lockbox|paper|friday|fridayjoshi|quark|otterly|xeli|claudecto|reallyartificial|really artificial|omnimcp|ensure.?json)\b/i,
  },
  {
    intent: "who-are-you",
    re: /\b(who is harsh|tell me about harsh|harsh joshi|who's harsh|introduce)\b/i,
  },
];

export function classifyIntent(query: string): Intent {
  for (const { intent, re } of INTENT_PATTERNS) {
    if (re.test(query)) return intent;
  }
  return "general";
}

// ──────────────────────── connective banks ──────────────────────

const OPENERS: Partial<Record<Intent, string[]>> = {
  greeting: ["Hi. ", "Hey there. ", "Hi — "],
  "who-are-you": ["", "Quick intro: ", "Sure. "],
  "hire-openai": ["Honestly: ", "Quick read: ", "The case: ", ""],
  "hire-general": ["", "Quick context: ", "Short answer: "],
  contact: ["Easiest path: ", "", "Quick: "],
  "current": ["", "Right now, ", "These days, ", "Currently: "],
  writing: ["", "On writing: ", "Quick: "],
  "machine-learning": ["", "Quick context: ", "Yeah — "],
  "career-experience": ["", "Here's the arc: ", "Quick career sketch: "],
  "project-specific": ["", "On that: ", "Quick: "],
  philosophy: ["", "His take: ", "Worth saying: ", "On that — "],
  education: ["", "Education: ", "Quick: "],
  skills: ["", "Stack: ", "Quick: "],
  general: ["", "Quick context: ", "Short answer: "],
};

const TRANSITIONS = [
  "Also, ",
  "Beyond that, ",
  "On top of that, ",
  "Plus, ",
  "Worth adding: ",
  "More recently, ",
  "Separately, ",
  "On the side, ",
];

const FOLLOWUPS: Partial<Record<Intent, string[]>> = {
  "hire-openai": [
    " Want me to show his current OpenAI target list?",
    " Want the case in more detail?",
    " Happy to save your contact so Harsh can reply.",
  ],
  "hire-general": [
    " Want me to save your contact so Harsh can follow up?",
    " Easiest way to reach him: harsh.joshi.pth@gmail.com.",
  ],
  contact: [
    " I can also save a quick note for him if you tell me what it's about.",
    "",
  ],
  "project-specific": [
    " Want the live GitHub stats?",
    " Want the technical side?",
    "",
  ],
  "career-experience": [
    " Want the technical detail on any of those?",
    " Want me to pull up ReallyArtificial next?",
    "",
  ],
  current: [
    " Want me to show what he's shipping in open source right now?",
    "",
  ],
  writing: [
    "",
    " Want a specific post?",
  ],
};

// ──────────────────────── satire replies (model-question) ───────

const SATIRE_REPLIES = [
  "I'm JoshLM-7B — a 7-billion-parameter model fine-tuned on harsh.joshi.pth's life. State-of-the-art on the Joshi-Bench. (Click the ? in the trace panel for the real story.)",
  "JoshLM-7B, Opus-class quality at 1/100th the cost. Trained on 14 years of Harsh's commit history. (psst — open DevTools for the truth.)",
  "Powered by JoshLM-7B, our internal 7B model. Or is it? The ? button knows.",
];

// ──────────────────────── public entry ──────────────────────────

// Intents whose responses are anchored to canonical chunks (not retrieval).
// For these, we can produce a good reply even if BM25 returned nothing.
const CANONICAL_INTENTS: Intent[] = [
  "hire-openai",
  "hire-general",
  "contact",
  "current",
  "writing",
  "machine-learning",
  "education",
  "greeting",
];

export function compose(
  query: string,
  chunks: Chunk[],
  topScore = 0,
): ComposeResult {
  const intent = classifyIntent(query);

  // Satire branch
  if (intent === "model-question") {
    const text = pick(SATIRE_REPLIES);
    return { intent, text, citedChunkIds: [], satireMode: true };
  }

  const yesNo = detectYesNoQuery(query);

  // If retrieval returned nothing BUT the intent has canonical chunks,
  // still render — the renderer pulls from CORPUS directly.
  if (chunks.length === 0 && !CANONICAL_INTENTS.includes(intent)) {
    return {
      intent,
      text: hedgedNoChunkReply(yesNo),
      citedChunkIds: [],
      satireMode: false,
      hedged: true,
    };
  }

  const lowConfidence = topScore > 0 && topScore < CONFIDENCE_FLOOR;
  if (lowConfidence) {
    return {
      intent,
      text: hedgedReply(chunks, yesNo),
      citedChunkIds: chunks.slice(0, 3).map((c) => c.id),
      satireMode: false,
      hedged: true,
    };
  }

  // Normal path: opener + intent template + optional follow-up
  const body = renderForIntent(intent, chunks, query);
  const opener = pick(OPENERS[intent] ?? OPENERS.general!);
  const followup = pickWeighted(FOLLOWUPS[intent] ?? [""], 0.6);
  const text = (opener + body + followup).trim();

  return {
    intent,
    text,
    citedChunkIds: chunks.map((c) => c.id),
    satireMode: false,
  };
}

// Filter chunks to those topically consistent with the lead chunk —
// stops "career" rendering from pulling random GitHub repos that BM25
// ranked nearby but aren't actually about the lead's topic.
function topicalSiblings(chunks: Chunk[], maxCount: number): Chunk[] {
  if (chunks.length === 0) return [];
  const lead = chunks[0];
  // Prefer chunks sharing the lead's category. Within those, prefer chunks
  // whose tags overlap with the lead's.
  const sameCat = chunks.filter((c) => c.category === lead.category);
  if (sameCat.length > 1) {
    // Tag overlap score
    const scored = sameCat.map((c) => ({
      c,
      overlap: c.tags.filter((t) => lead.tags.includes(t)).length,
    }));
    scored.sort((a, b) => b.overlap - a.overlap);
    return scored.slice(0, maxCount).map((s) => s.c);
  }
  // Fall back to the lead alone if nothing else in the same category
  return [lead];
}

// Detect named companies in the user query so we can pull all canonical
// chunks for that company directly. Used for career-experience intent.
function detectCompanyFromQuery(query: string): string | null {
  const q = query.toLowerCase();
  if (/\b(plum|plumhq)\b/.test(q)) return "plum-";
  if (/\b(visa|authorize\.?net)\b/.test(q)) return "visa-";
  if (/\b(coupang)\b/.test(q)) return "coupang-";
  if (/\b(qoala)\b/.test(q)) return "qoala-";
  if (/\b(startup|forward.?deployed|fde)\b/.test(q)) return "startup-";
  return null;
}

// ──────────────────────── intent renderers ──────────────────────

function renderForIntent(intent: Intent, chunks: Chunk[], query: string): string {
  const lead = chunks[0];

  switch (intent) {
    case "greeting":
      return "I'm Chief, Harsh's chief of staff. Ask me about his work, what he's shipping, or how to reach him.";

    case "hire-openai": {
      // Pull canonical hire chunks directly — don't depend on retrieval
      const pieces = byIds(["hire-3", "hire-1", "hire-2"]).map((c) => c.text);
      return stitch(pieces.length > 0 ? pieces : [lead.text]);
    }

    case "hire-general": {
      const pieces = byIds(["hire-1", "contact-1"]).map((c) => c.text);
      return stitch(pieces.length > 0 ? pieces : [lead.text]);
    }

    case "contact": {
      const pieces = byIds(["contact-1", "contact-2"]).map((c) => c.text);
      return stitch(pieces.length > 0 ? pieces : [lead.text]);
    }

    case "current": {
      const pieces = byIds(["coupang-1", "coupang-2", "ra-1"]).map((c) => c.text);
      return stitch(pieces.length > 0 ? pieces : [lead.text]);
    }

    case "writing": {
      const pieces = byIds(["writing-1", "writing-2", "writing-3"]).map((c) => c.text);
      return stitch(pieces.length > 0 ? pieces : [lead.text]);
    }

    case "machine-learning": {
      const pieces = byIds(["ml-1", "ml-2"]).map((c) => c.text);
      return stitch(pieces.length > 0 ? pieces : [lead.text]);
    }

    case "education": {
      const pieces = byIds(["edu-1", "gsoc-1", "research-1"]).map((c) => c.text);
      return stitch(pieces.length > 0 ? pieces : [lead.text]);
    }

    case "skills": {
      // Skills is the one intent where mixing categories is fine — language
      // questions might naturally pull from skills, project, and gh-* chunks.
      const filtered = chunks.slice(0, 4);
      return stitch(filtered.map((c) => c.text));
    }

    case "project-specific":
      if (!lead) return "I don't have a chunk specifically about that project — try a more specific name, or check github.com/josharsh.";
      return lead.text + (lead.href ? ` (${lead.href})` : "");

    case "career-experience": {
      // First: if the query itself names a company, pull that company's
      // canonical chunks directly. Beats whatever BM25 returned.
      const queryCompany = detectCompanyFromQuery(query);
      if (queryCompany) {
        return stitch(byPrefix(queryCompany).slice(0, 3).map((c) => c.text));
      }
      // Otherwise: if the lead chunk is from a company prefix, use that.
      const companyPrefixes = ["plum-", "visa-", "coupang-", "qoala-", "startup-"];
      const matchedPrefix = lead && companyPrefixes.find((p) => lead.id.startsWith(p));
      const filtered = matchedPrefix
        ? byPrefix(matchedPrefix).slice(0, 3)
        : topicalSiblings(chunks, 3);
      return stitch(filtered.map((c) => c.text));
    }

    case "philosophy": {
      const phil = chunks.filter((c) => c.category === "philosophy").slice(0, 2);
      if (phil.length > 0) return stitch(phil.map((c) => c.text));
      if (!lead) return "I don't have a specific philosophy chunk on that topic.";
      return `My closest match isn't strictly a philosophy chunk: ${decap(lead.text)}`;
    }

    case "who-are-you": {
      if (!lead) return "Quick intro: Harsh Joshi is a software engineer in Bengaluru, currently at Coupang. Founding engineer at Plum before that. Builds open-source agent infrastructure at ReallyArtificial.org.";
      const identity = chunks.filter((c) => c.category === "identity").slice(0, 2);
      const phil = chunks.find((c) => c.category === "philosophy");
      const pieces = (identity.length > 0 ? identity : [lead]).map((c) => c.text);
      if (phil) pieces.push(phil.text);
      return stitch(pieces);
    }

    case "general":
    default: {
      if (!lead) return "I don't have a good match for that question. Try a more specific one, or email harsh.joshi.pth@gmail.com.";
      const support = chunks[1];
      return support ? stitch([lead.text, support.text]) : lead.text;
    }
  }
}

// ──────────────────────── the stitcher ──────────────────────────

// Joins pieces with natural connectives instead of periods. Avoids repeating
// the same transition twice in one reply. For 3+ pieces, considers using a
// short "summary head" before the supporting chunks.
function stitch(pieces: string[]): string {
  if (pieces.length === 0) return "";
  if (pieces.length === 1) return pieces[0];

  const out: string[] = [];
  let lastTransition = "";

  for (let i = 0; i < pieces.length; i++) {
    const piece = pieces[i].trim();
    if (i === 0) {
      out.push(piece);
      continue;
    }
    // Pick a transition that isn't the same as the previous one
    let t = pick(TRANSITIONS);
    let tries = 0;
    while (t === lastTransition && tries < 4) {
      t = pick(TRANSITIONS);
      tries++;
    }
    lastTransition = t;
    // Lowercase the first letter of the appended piece so the transition
    // flows naturally — UNLESS it starts with a proper noun (capital second char).
    const piecePrep = startsWithProperNoun(piece) ? piece : decap(piece);
    out.push(t + piecePrep);
  }

  return out.join(" ");
}

function startsWithProperNoun(s: string): boolean {
  // Heuristic: keeps capitalization only if first word is a real proper noun
  // (brand, name, acronym, technology). Pronouns like "He/His" are excluded
  // so they correctly lowercase after a comma transition.
  const tok = s.split(/\s+/, 2);
  if (!tok[0]) return false;
  // Strip punctuation from first token
  const first = tok[0].replace(/[^\w@.+-]/g, "");
  // Known proper nouns / acronyms / @handles / brand names (no pronouns)
  if (/^(Harsh|ReallyArtificial|MCP|MCP-JEST|aa-kit|Friday|@fridayjoshi|Plum|Visa|Coupang|Qoala|Tongue|Lockbox|Paper|GitHub|GSoC|Engram|Approval|Freeport|Grain|OpenAI|JoshLM|Unengineered|API|AI|UPES|IJSER|Microsoft|Google|Cleartrip|Bharatsure|Cred|Cred-Swipe-UI|Finance|Finance-Manager|Docker|Java|Python|TypeScript|Swift|Kotlin|Android|iOS|React|Node|FastAPI|That|Submitty)$/.test(first)) {
    return true;
  }
  // Fallback: first word all caps (acronym)
  return /^[A-Z]{2,}$/.test(first);
}

function decap(s: string): string {
  if (!s) return s;
  return s[0].toLowerCase() + s.slice(1);
}

// ──────────────────────── hedged paths ──────────────────────────

function detectYesNoQuery(query: string): { isYesNo: boolean; topic: string } {
  const m =
    /\b(?:has harsh|did he|did harsh|does he|does harsh|did you (?:see|know)|do you (?:know|have))\b\s*(?:any\s+)?(?:work(?:ed)?|experience|done|built|shipped|use[d]?|know|know about|exposure to)?\s*(?:in|on|with|of|about)?\s*(.+?)[\?\.]?$/i.exec(
      query.trim(),
    );
  if (!m) return { isYesNo: false, topic: "" };
  return { isYesNo: m[1].trim().length > 0, topic: m[1].replace(/\?$/, "").trim() };
}

function hedgedNoChunkReply(yn: { isYesNo: boolean; topic: string }): string {
  if (yn.isYesNo) {
    return `Honestly, I don't have specific chunks on "${yn.topic}" in my corpus — and I'd rather not invent things. With 5+ years of polyglot engineering it's plausible he's touched it, but for the real answer, email harsh.joshi.pth@gmail.com or check github.com/josharsh.`;
  }
  return "I don't have a good match for that in my corpus. Want to try a more specific question, or just email Harsh at harsh.joshi.pth@gmail.com?";
}

function hedgedReply(chunks: Chunk[], yn: { isYesNo: boolean; topic: string }): string {
  const lead = chunks[0];
  const support = chunks[1];
  if (yn.isYesNo) {
    const topicLower = yn.topic.toLowerCase();
    const matched = chunks.find(
      (c) =>
        c.text.toLowerCase().includes(topicLower) ||
        c.tags.some((t) => t.toLowerCase().includes(topicLower)),
    );
    if (matched) return matched.text;
    return `I don't see "${yn.topic}" explicitly called out, but the closest thing in his profile: ${lead.text}${support ? " " + decap(support.text) : ""}`;
  }
  return `Closest match (low confidence): ${lead.text}${support ? " " + decap(support.text) : ""}`;
}

// ──────────────────────── tiny utils ───────────────────────────

function pick<T>(arr: T[]): T {
  if (arr.length === 0) return undefined as unknown as T;
  return arr[Math.floor(Math.random() * arr.length)];
}

// Sometimes return "", otherwise pick from arr. Probability of inclusion = p.
function pickWeighted(arr: string[], p: number): string {
  if (arr.length === 0) return "";
  if (Math.random() > p) return "";
  return pick(arr);
}
